/**
 * The keyboard pass, as a table.
 *
 * Galdino's spec, for the "experts" he wants to create. It was a chain of
 * `event.key ===` inside the screen, which is where an alias goes missing and
 * where a legend drifts from what the keys do -- the two failures this file
 * exists to make visible.
 *
 * The mapping is the one the screen already draws: the steps are a horizontal
 * strip, so they are ← →; the apontamentos are a vertical list, so they are
 * ↑ ↓. Tab does what ↓ does, because it is also how somebody arrives from
 * outside the pass.
 */

/** What a key means while the focus is on an apontamento. */
export type RowIntent = 'fix' | 'dismiss' | 'undo' | 'next-row' | 'previous-row'

/**
 * @param command Ctrl, ⌘ or Alt held with the key.
 */
export function rowIntent(key: string, shiftKey: boolean, command = false): RowIntent | undefined {
  /*
   * Held with Ctrl, ⌘ or Alt, a key is the browser's or the system's: ⌘A
   * selects everything, Ctrl+X cuts, Ctrl+Tab changes tab. Read as answers,
   * they recorded decisions nobody gave. The one they share with this screen
   * is ⌘Z / Ctrl+Z, which means undo everywhere.
   */
  if (command) return isUndo(key) ? 'undo' : undefined

  if (key === 'Tab') return shiftKey ? 'previous-row' : 'next-row'
  if (key === 'ArrowDown') return 'next-row'
  if (key === 'ArrowUp') return 'previous-row'

  if (key === 'Enter' || key === 'a' || key === 'A') return 'fix'
  /*
   * Backspace and Delete alongside X.
   *
   * Galdino reached for both unprompted, which is what a key that removes
   * something is for. Escape used to be here and is deliberately not: see
   * `screenIntent`.
   */
  if (key === 'x' || key === 'X' || key === 'Backspace' || key === 'Delete') return 'dismiss'
  /*
   * Z takes an answer back, on its own or as ⌘Z / Ctrl+Z.
   *
   * Beside X on the keyboard, which is safe both ways: Z on a row still to
   * answer has nothing to undo, and X on an answered one asks nothing.
   */
  if (isUndo(key)) return 'undo'

  return undefined
}

const isUndo = (key: string) => key === 'z' || key === 'Z'

/** What a key means anywhere on the conferência, row or not. */
export type ScreenIntent = 'previous-step' | 'next-step' | 'clear-pins' | 'go-to-requisition'

/**
 * The key that focuses "Ir para conferência" (#501).
 *
 * The slash, as in most places a key jumps to a search box, and a character
 * rather than a letter so it cannot be mistaken for an answer: A and X decide
 * an apontamento. Named because the field and the legend both print it.
 */
export const GO_TO_REQUISITION_KEY = '/'

/**
 * @param command Ctrl, ⌘ or Alt held with the key: Alt+← is the browser's
 *   back, ⌘← the start of a line, and neither is a step.
 */
export function screenIntent(key: string, command = false): ScreenIntent | undefined {
  if (command) return undefined
  if (key === 'ArrowLeft') return 'previous-step'
  if (key === 'ArrowRight') return 'next-step'
  /*
   * Escape leaves rather than answers.
   *
   * It used to record "não é divergência", which is the one thing Escape means
   * nowhere else. Every application spends that key on getting out of the
   * state you are in, so a pharmacist who had pinned three rows to compare
   * them and wanted out pressed it and answered a difference instead.
   */
  if (key === 'Escape') return 'clear-pins'
  if (key === GO_TO_REQUISITION_KEY) return 'go-to-requisition'
  return undefined
}
